import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScenarioStat } from "@/types/admin.types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Trophy, Target, TrendingUp, Medal, Award, Star } from "lucide-react";

interface TopScenariosTableProps {
  data: ScenarioStat[];
}

export function TopScenariosTable({ data }: TopScenariosTableProps) {
  // Sort data by count descending just in case
  const sortedData = [...data].sort((a, b) => b.count - a.count);
  const maxCount = sortedData.length > 0 ? sortedData[0].count : 1;

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return <Star className="h-4 w-4 text-slate-300" />;
    }
  };

  return (
    <Card className="col-span-1 shadow-sm border-border/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Most Practiced Scenarios
            </CardTitle>
            <CardDescription>
              Top scenarios practiced by your team
            </CardDescription>
          </div>
          <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">
            <TrendingUp className="h-3 w-3 mr-1" />
            Top {sortedData.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[80px] text-center">Rank</TableHead>
                <TableHead>Scenario Category</TableHead>
                <TableHead className="text-right">Sessions</TableHead>
                <TableHead className="w-[30%] hidden sm:table-cell">Popularity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((scenario, index) => {
                const percentage = Math.round((scenario.count / maxCount) * 100);
                return (
                  <TableRow key={scenario.category} className="hover:bg-muted/50 transition-colors">
                    <TableCell className="text-center font-medium">
                      <div className="flex justify-center items-center">
                        {getRankIcon(index)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {scenario.category}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="font-semibold">
                        {scenario.count}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <Progress value={percentage} className="h-2" />
                        <span className="text-xs text-muted-foreground w-8 text-right">
                          {percentage}%
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                    No scenario data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
